import { useQuery } from "@tanstack/react-query";
import { PageHeading } from "@/components/ui/page-heading";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/lib/auth-context";
import {
  UserRole,
  formatCurrency,
  formatDate,
} from "@employee-management/common";
import { apiService } from "@/lib/api-service";
import type {
  SuperAdminDashboard,
  ManagerDashboard,
  SupervisorDashboard,
} from "@/types/reports.types";
import {
  Clock,
  DollarSign,
  Building,
  Activity,
  TrendingUp,
  Calendar,
  AlertCircle,
} from "lucide-react";

export function HomePageView() {
  const { user, hasRole } = useAuth();

  // Fetch dashboard data based on user role
  const { data: superAdminData } = useQuery({
    queryKey: ["dashboard", "superadmin"],
    queryFn: () =>
      apiService.get<SuperAdminDashboard>("/reports/dashboard/superadmin"),
    enabled: hasRole(UserRole.SUPERADMIN),
  });

  const { data: managerData } = useQuery({
    queryKey: ["dashboard", "manager"],
    queryFn: () =>
      apiService.get<ManagerDashboard>("/reports/dashboard/manager"),
    enabled: hasRole(UserRole.MANAGER),
  });

  const { data: supervisorData } = useQuery({
    queryKey: ["dashboard", "supervisor"],
    queryFn: () =>
      apiService.get<SupervisorDashboard>("/reports/dashboard/supervisor"),
    enabled: hasRole(UserRole.SUPERVISOR),
  });

  const getDashboardData = () => {
    if (hasRole(UserRole.SUPERADMIN)) return superAdminData;
    if (hasRole(UserRole.MANAGER)) return managerData;
    return null; // Supervisor has their own dedicated view
  };

  const getOverallStats = () => {
    if (hasRole(UserRole.SUPERADMIN) && superAdminData)
      return superAdminData.overallStats;
    if (hasRole(UserRole.MANAGER) && managerData)
      return managerData.overallStats;
    return null;
  };

  const getPendingApprovals = () => {
    if (hasRole(UserRole.MANAGER) && managerData)
      return managerData.pendingApprovals;
    else if (hasRole(UserRole.SUPERVISOR) && supervisorData) {
      return supervisorData.pendingApprovals;
    }
    return 0;
  };

  const dashboardData = getDashboardData();
  const overallStats = getOverallStats();
  const pendingApprovals = getPendingApprovals();

  // Supervisor dedicated view
  if (user?.role === UserRole.SUPERVISOR) {
    return (
      <div className="space-y-6">
        <PageHeading
          title="Dashboard"
          subtitle={`Welcome back, ${user?.name}. Here's your team overview.`}
          actions={
            <p className="text-sm text-muted-foreground">Last 30 days</p>
          }
        />

        {/* Supervisor Quick Stats */}
        {supervisorData && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Active Projects */}
            <Card className="!bg-muted/20">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Active Projects
                </CardTitle>
                <Building className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {supervisorData.activeProjects.length}
                </div>
                <p className="text-xs text-muted-foreground">
                  Projects you manage
                </p>
              </CardContent>
            </Card>

            {/* Total Hours */}
            <Card className="!bg-muted/20">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Team Hours
                </CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {supervisorData.activeProjects
                    .reduce((sum, project) => sum + project.totalHours, 0)
                    .toFixed(1)}
                  h
                </div>
                <p className="text-xs text-muted-foreground">Last 30 days</p>
              </CardContent>
            </Card>

            {/* Pending Approvals */}
            <Card className="!bg-muted/20">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Pending Approvals
                </CardTitle>
                <AlertCircle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {supervisorData.pendingApprovals}
                </div>
                <p className="text-xs text-muted-foreground">Requires review</p>
              </CardContent>
            </Card>
          </div>
        )}
        {/* Supervisor-specific content */}
        {supervisorData && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Active Projects */}
            <Card className="!bg-muted/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building className="h-5 w-5" />
                  Your Active Projects
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {supervisorData.activeProjects.map((project) => (
                  <div
                    key={project.id}
                    className="flex items-center justify-between p-3 border rounded-lg bg-muted/20"
                  >
                    <div>
                      <h4 className="font-medium">{project.name}</h4>
                      <p className="text-sm text-muted-foreground">
                        {project.clientName} • {project.employeeCount} employees
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">
                        {project.totalHours.toFixed(1)}h
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {project.entryCount} entries
                      </p>
                    </div>
                  </div>
                ))}
                {supervisorData.activeProjects.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Building className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No active projects found</p>
                    <p className="text-sm">
                      Projects you create or have access to will appear here
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card className="!bg-muted/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Recent Team Activity
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {supervisorData.recentActivity.map((activity) => (
                  <div
                    key={activity.id}
                    className="flex items-center justify-between p-3 border rounded-lg bg-muted/20"
                  >
                    <div>
                      <h4 className="font-medium">{activity.project.name}</h4>
                      <p className="text-sm text-muted-foreground">
                        {activity.employees
                          .map((emp) => emp.employee.name)
                          .join(", ")}
                      </p>
                    </div>
                    <div className="text-right">
                      <Badge
                        variant={
                          activity.approvalStatus === "APPROVED"
                            ? "default"
                            : "secondary"
                        }
                      >
                        {activity.approvalStatus}
                      </Badge>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDate(new Date(activity.date))}
                      </p>
                    </div>
                  </div>
                ))}
                {supervisorData.recentActivity.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No recent activity</p>
                    <p className="text-sm">Overtime entries will appear here</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    );
  }

  // Regular dashboard for SuperAdmin and Manager
  return (
    <div className="space-y-6">
      <PageHeading
        title="Dashboard"
        subtitle={`Welcome back, ${user?.name}. Here's what's happening in your workspace.`}
        actions={<p className="text-sm text-muted-foreground">Last 30 days</p>}
      />

      {/* Quick Stats Cards */}
      {dashboardData && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Hours */}
          <Card className="!bg-muted/20">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Hours</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {overallStats?.totalHours?.toFixed(1) || "0.0"}h
              </div>
              <p className="text-xs text-muted-foreground">Last 30 days</p>
            </CardContent>
          </Card>

          {/* Total Amount */}
          {(hasRole(UserRole.SUPERADMIN) || hasRole(UserRole.MANAGER)) && (
            <Card className="!bg-muted/20">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Total Amount
                </CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatCurrency(overallStats?.totalAmount || 0)}
                </div>
                <p className="text-xs text-muted-foreground">Last 30 days</p>
              </CardContent>
            </Card>
          )}

          {/* Total Entries */}
          <Card className="!bg-muted/20">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Entries
              </CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {overallStats?.totalEntries || 0}
              </div>
              <p className="text-xs text-muted-foreground">Last 30 days</p>
            </CardContent>
          </Card>

          {/* Pending Approvals */}
          <Card className="!bg-muted/20">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Pending Approvals
              </CardTitle>
              <AlertCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{pendingApprovals}</div>
              <p className="text-xs text-muted-foreground">Requires review</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Role-specific content */}
      {hasRole(UserRole.SUPERADMIN) && superAdminData && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Department Stats */}
          <Card className="!bg-muted/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="h-5 w-5" />
                Department Overview
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {superAdminData.departmentStats.map((dept) => (
                <div
                  key={dept.department}
                  className="flex items-center justify-between p-3 border rounded-lg bg-muted/20"
                >
                  <div>
                    <h4 className="font-medium">{dept.department}</h4>
                    <p className="text-sm text-muted-foreground">
                      {dept.employeeCount} employees
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">{dept.totalHours.toFixed(1)}h</p>
                    <p className="text-sm text-muted-foreground">
                      {formatCurrency(dept.totalAmount)}
                    </p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Recent Projects */}
          <Card className="!bg-muted/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Recently Updated Projects
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {superAdminData.recentProjects.map((project) => (
                <div
                  key={project.id}
                  className="flex items-center justify-between p-3 border rounded-lg bg-muted/20"
                >
                  <div>
                    <h4 className="font-medium">{project.name}</h4>
                    <p className="text-sm text-muted-foreground">
                      {project.clientName} • {project.handlerMemberRef.name}
                    </p>
                  </div>
                  <div className="text-right">
                    <Badge
                      variant={
                        project.status === "ACTIVE" ? "default" : "secondary"
                      }
                    >
                      {project.status}
                    </Badge>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatDate(new Date(project.updatedAt))}
                    </p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      {hasRole(UserRole.MANAGER) && managerData && (
        <div className="grid grid-cols-1 gap-6">
          {/* Project Stats */}
          <Card className="!bg-muted/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="h-5 w-5" />
                Project Performance
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {managerData.projectStats.slice(0, 5).map((project) => (
                <div
                  key={project.id}
                  className="flex items-center justify-between p-3 border rounded-lg bg-muted/20"
                >
                  <div>
                    <h4 className="font-medium">{project.name}</h4>
                    <p className="text-sm text-muted-foreground">
                      {project.clientName} • {project.entryCount} entries
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">
                      {project.totalHours.toFixed(1)}h
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {formatCurrency(project.totalAmount)}
                    </p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
